<i18n>
{
  "en": {
    "noPriceData": "Price recommendations are being generated",
    "noPeriodData": "There are no available periods to optimize",
    "savePricesError": "Error saving prices. Please, try it later.",
    "savePricesSuccess": "Saved prices successfully",
    "recommendationPeriod": "Price recommendations"
  },
  "es": {
    "noPriceData": "Las recomendaciones de precios se están generando",
    "noPeriodData": "No hay periodos de optimización disponibles",
    "savePricesError": "Error guardando precios. Inténtelo de nuevo más tarde.",
    "savePricesSuccess": "Se han guardado los precios correctamente",
    "recommendationPeriod": "Recomendaciones de precios"
  }
}
</i18n>

<template>
  <div>
    <dynameat-skeleton v-if="isLoading" skeleton-type="manualConfigList" :container="false"/>
    <div v-else>
      <v-list class="pb-14" v-if="!isLoading">
        <draggable v-model="items.itemListSorted" v-bind="dragOptions" :move="onMove" @end="onEnd"
        filter=".non-draggable" :disabled="!isEditable">
          <transition-group type="transition">
            <div v-for="item in items.itemListSorted" :key="item.guid"
                 class="mb-3 mb-md-2">
              <item-card v-on="$listeners" :key="item.guid" :item="item"
                         :class="{'non-draggable': !isProduct(item)}"
                         :is-first-item="isFirstItem(item.guid)" :is-last-item="isLastItem(item.guid)"
                         :is-child-product="isChildProduct(item.guid)" :isEditable="isEditable"/>
            </div>
          </transition-group>
        </draggable>
      </v-list>
    </div>
  </div>
</template>

<script>
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapGetters } from '@core/helpers/decorators';
import { Prop } from 'vue-property-decorator';
import draggable from 'vuedraggable';
import { ProductRecommendation, SubsectionRecommendation } from '../models';
import ItemCard from './ItemCard.vue';

@Component
@Components({ DynameatSkeleton, ItemCard, draggable })
@MapGetters(['isLoading'])
export default class ItemList extends Vue {
  isConfirmationAllowed = false;

  dragIncrement = 0;

  dragItem = null;

  dragOriginalIndex = 0;

  maxDistances = null;

  @Prop({ Array, default: [] }) itemList;

  @Prop({ type: Boolean, default: false }) isEditable;

  get items() {
    const itemListSorted = [];
    const lastItems = [];
    const firstItems = [];
    const childProducts = [];

    this.itemList.forEach((item, index, list) => {
      if (this.isFirstInList(item, index) || this.isProductBelowSubsection(item, index, list)) {
        firstItems.push(item.guid); // Disable top arrow
      }
      if (this.isProductAboveSubsection(item, index, list)) {
        lastItems.push(item.guid); // Disable bottom arrow
      }
      itemListSorted.push(item);
      if (item.products) this.processItemProducts(item.products, itemListSorted, lastItems, firstItems, childProducts);
    });
    const lastItemGuid = this.getLastElement(itemListSorted)?.guid;
    if (lastItemGuid && !lastItems.includes(lastItemGuid)) {
      lastItems.push(lastItemGuid); // Last element of Section
    }

    return {
      itemListSorted, lastItems, firstItems, childProducts
    };
  }

  get dragOptions() {
    // Fallback class applies only in "mobile mode" to avoid the bottom of the list bug when sorting
    if (this.$vuetify.breakpoint.smAndDown) {
      return {
        fallbackOnBody: true,
        fallbackClass: 'dragged',
        delay: 100,
      };
    }
    return {};
  }

  onEnd() {
    let distance = 0;
    if (this.dragIncrement !== 0) {
      const direction = this.dragIncrement > 0 ? 'down' : 'up';
      // We need to find the maximum travel distance, for that we find the next first or last element.
      let currentItem = null;
      let i;
      for (i = 0; i < Math.abs(this.dragIncrement); i++) {
        if (direction === 'up') {
          currentItem = this.items.itemListSorted[this.dragOriginalIndex - i];
          if (this.isFirstItem(currentItem.guid)) {
            distance = i + 1;
            break;
          }
        } else {
          currentItem = this.items.itemListSorted[this.dragOriginalIndex + i];
          if (this.isLastItem(currentItem.guid)) {
            distance = i + 1;
            break;
          }
        }
      }
      if (i === Math.abs(this.dragIncrement)) {
        distance = i;
      }
      distance = Math.min(distance, this.maxDistances[direction]);
      const item = this.dragItem;
      this.dragItem = null;
      this.dragIncrement = 0;
      this.dragOriginalIndex = 0;
      if (distance !== 0) {
        this.$emit('dragged', { item, distance, direction });
      }
    }
    this.dragItem = null;
    this.dragIncrement = 0;
    this.dragOriginalIndex = 0;
  }

  onMove(evt) {
    if (evt.draggedContext.element instanceof SubsectionRecommendation) {
      return false;
    }
    if (!this.dragItem) {
      this.dragItem = evt.draggedContext.element;
      this.maxDistances = this.maxDraggedDistances(this.dragItem);
      this.dragOriginalIndex = evt.draggedContext.index;
    }
    this.dragIncrement = evt.draggedContext.futureIndex - evt.draggedContext.index;
    const direction = this.dragIncrement > 0 ? 'down' : 'up';
    return this.maxDistances[direction] >= Math.abs(this.dragIncrement);
  }

  maxDraggedDistances(item) {
    const res = { up: 0, down: 0 };
    let i = 0;
    let currentItemUp = null;
    let currentItemDown = null;
    const stop = { up: false, down: false };
    const currentIndex = this.items.itemListSorted.findIndex((product) => product.guid === item.guid);
    for (i = 0; i < Math.abs(this.items.itemListSorted.length); i++) {
      currentItemUp = this.items.itemListSorted[currentIndex - i];
      if (currentItemUp && this.isFirstItem(currentItemUp.guid) && !stop.up) {
        stop.up = true;
        res.up = i;
      }
      currentItemDown = this.items.itemListSorted[currentIndex + i];
      if (currentItemDown && this.isLastItem(currentItemDown.guid) && !stop.down) {
        stop.down = true;
        res.down = i;
      }
    }
    return res;
  }

  processItemProducts(products, itemListSorted, lastItems, firstItems, childProducts) {
    const currentList = itemListSorted;
    products.sort((a, b) => (a.order > b.order ? 1 : -1)).forEach((product, index) => {
      if (this.isFirstInList(product, index)) firstItems.push(product.guid);
      childProducts.push(product.guid);
      currentList.push(product);
    });
    lastItems.push(this.getLastElement(currentList).guid); // Last element of Subsection
  }

  isProductBelowSubsection(item, index, list) {
    return item instanceof ProductRecommendation && list[index - 1] instanceof SubsectionRecommendation;
  }

  isProductAboveSubsection(item, index, list) {
    return item instanceof ProductRecommendation && list[index + 1] instanceof SubsectionRecommendation;
  }

  getLastElement(list) {
    return list[list.length - 1];
  }

  isFirstInList(item, index) {
    return item instanceof ProductRecommendation && (index === 0 || item.order === 0);
  }

  isLastItem(itemGuid) {
    return this.items.lastItems.includes(itemGuid);
  }

  isFirstItem(itemGuid) {
    return this.items.firstItems.includes(itemGuid);
  }

  isChildProduct(itemGuid) {
    return this.items.childProducts.includes(itemGuid);
  }

  isProduct(item) {
    return item instanceof ProductRecommendation;
  }
}
</script>

<style scoped>
.dragged {
  font-family: Roboto, sans-serif;
}
</style>
